import { memo, useMemo } from 'react';
import { Badge, Button, DataTable, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Brain, Database, FlaskConical, Package, Trash2, Waypoints } from 'lucide-react';

import { PERMISSIONS } from '../../constants/permissions';
import Can from '../guards/Can';

const TYPE_ICONS = Object.freeze({
  dataset: Database,
  model: Brain,
  event_bus: Waypoints,
  sandbox: FlaskConical,
});

/** Badge color of each type (the prototype tints it: Dataset is blue). */
const TYPE_VARIANTS = Object.freeze({
  dataset: 'lightBlue',
  model: 'purple',
  event_bus: 'orange',
  sandbox: 'limeGreen',
});

const ACTIONS_COLUMN_CLASS = 'w-12 text-right';
const getResourceId = (resource) => resource.id;

/**
 * Resources a member reaches: resource (with its type icon), type, access, who granted it and how
 * (direct, via a role or a group), when, and a trailing remove icon. Only direct access can be
 * removed here; the icon of a role or group access is disabled and says where to change it.
 *
 * @param {object} props
 * @param {object[]} props.resources UI resources (see `useMemberResources`)
 * @param {string} props.locale
 * @param {(resource: object) => void} props.onRemove
 * @param {import('react').ReactNode} props.emptyMessage
 */
function MemberResourcesTable({ resources, locale, onRemove, emptyMessage }) {
  const { t } = useTranslation('people-access');

  const columns = useMemo(() => {
    const date = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
    return [
      {
        key: 'name',
        label: t('memberTabs.resources.columns.resource'),
        render: (_, resource) => {
          const Icon = TYPE_ICONS[resource.type] ?? Package;
          return (
            <span className="flex items-center gap-2 text-sm text-content">
              <Icon className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
              <span className="truncate">{resource.name}</span>
            </span>
          );
        },
      },
      {
        key: 'type',
        label: t('memberTabs.resources.columns.type'),
        render: (value) => (
          <Badge variant={TYPE_VARIANTS[value] ?? 'neutral'}>{t(`memberTabs.resources.types.${value}`)}</Badge>
        ),
      },
      { key: 'access', label: t('memberTabs.resources.columns.access') },
      {
        key: 'grantedBy',
        label: t('memberTabs.resources.columns.grantedBy'),
        render: (_, resource) => (
          <span className="flex flex-col text-sm">
            <span className="text-content">{resource.grantedBy}</span>
            <span className="text-xs text-content-muted">
              {t(`memberTabs.resources.sources.${resource.source}`, { via: resource.via })}
            </span>
          </span>
        ),
      },
      {
        key: 'grantedAt',
        label: t('memberTabs.resources.columns.grantedOn'),
        render: (value) => (value ? date.format(new Date(value)) : ''),
      },
      {
        key: 'actions',
        label: '',
        headerClassName: ACTIONS_COLUMN_CLASS,
        cellClassName: ACTIONS_COLUMN_CLASS,
        render: (_, resource) => (
          <Can permission={PERMISSIONS.MEMBERS_MANAGE_ACCESS}>
            {resource.isDirect ? (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={Trash2}
                aria-label={t('memberTabs.resources.removeAria', { name: resource.name })}
                onClick={() => onRemove(resource)}
              />
            ) : (
              <Tooltip content={t('memberTabs.resources.notDirect')}>
                <span
                  role="group"
                  tabIndex={0}
                  aria-label={t('memberTabs.resources.removeDisabledAria', { name: resource.name })}
                  className="inline-flex cursor-not-allowed opacity-50"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={Trash2}
                    disabled
                    aria-label={t('memberTabs.resources.removeAria', { name: resource.name })}
                  />
                </span>
              </Tooltip>
            )}
          </Can>
        ),
      },
    ];
  }, [t, locale, onRemove]);

  return (
    <div role="region" tabIndex={0} aria-label={t('memberTabs.resources.tableAria')} className="scrollbar-aimsos-auto-sm overflow-x-auto rounded-lg border border-edge focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50">
      <DataTable columns={columns} data={resources} getRowId={getResourceId} emptyMessage={emptyMessage} />
    </div>
  );
}

export default memo(MemberResourcesTable);
